import { Link } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { DISCORD_INVITE_URL, X_URL, BLUESKY_URL, FACEBOOK_URL } from '@/lib/constants';

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function BlueskyIcon() {
  return (
    <svg viewBox="0 0 600 530" className="h-4 w-4 fill-current" aria-hidden="true">
      <path d="M135.72 44.03C202.216 93.951 273.74 195.17 300 249.49c26.262-54.316 97.782-155.54 164.28-205.46 47.98-36.021 125.72-63.892 125.72 24.795 0 17.712-10.155 148.79-16.111 170.07-20.703 73.984-96.144 92.854-163.25 81.433 117.29 19.964 147.14 86.092 82.697 152.22-122.39 125.59-175.94-31.511-189.63-71.766-2.514-7.406-3.6935-10.877-3.706-7.994-.012-2.883-1.1922.588-3.6068 7.994-13.694 40.255-67.244 197.36-189.63 71.766-64.443-66.128-34.591-132.26 82.697-152.22-67.108 11.421-142.55-7.449-163.25-81.433C10.155 218.12 0 87.043 0 69.331c0-88.687 77.742-60.816 125.72-24.795z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
      <path d="M22 12.06C22 6.505 17.523 2 12 2S2 6.505 2 12.06c0 5.02 3.657 9.184 8.438 9.94v-7.03H7.898v-2.91h2.54V9.845c0-2.522 1.492-3.915 3.777-3.915 1.094 0 2.238.196 2.238.196v2.475h-1.26c-1.243 0-1.63.775-1.63 1.57v1.89h2.773l-.443 2.91h-2.33V22c4.78-.756 8.437-4.92 8.437-9.94z" />
    </svg>
  );
}

type FooterLink = {
  key: string;
  label: React.ReactNode;
  tooltip: string;
  href?: string;
  to?: string;
  external?: boolean;
};

const FOOTER_LINKS: FooterLink[] = [
  { key: 'x', label: <XIcon />, tooltip: 'Follow us on X', href: X_URL, external: true },
  {
    key: 'bluesky',
    label: <BlueskyIcon />,
    tooltip: 'Follow us on Bluesky',
    href: BLUESKY_URL,
    external: true,
  },
  {
    key: 'facebook',
    label: <FacebookIcon />,
    tooltip: 'Follow us on Facebook',
    href: FACEBOOK_URL,
    external: true,
  },
  {
    key: 'app',
    label: 'Get the App',
    tooltip: 'Download Polymyth for iOS or Android',
    to: '/get-the-app',
  },
  {
    key: 'discord',
    label: 'Community',
    tooltip: 'Join our Discord community',
    href: DISCORD_INVITE_URL,
    external: true,
  },
  { key: 'about', label: 'About', tooltip: 'Learn about Polymyth', to: '/about' },
  { key: 'faq', label: 'FAQ', tooltip: 'Frequently asked questions', to: '/faq' },
  { key: 'terms', label: 'Terms', tooltip: 'Terms of service', to: '/tos' },
  { key: 'privacy', label: 'Privacy', tooltip: 'Privacy policy', to: '/privacy' },
  {
    key: 'delete',
    label: 'Delete Account',
    tooltip: 'Request account deletion',
    to: '/delete-account',
  },
];

export default function Footer() {
  return (
    <footer className="border-t border-border/40 bg-background/50">
      <div className="mx-auto max-w-6xl px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-lg text-primary">☥</span>
          <span className="text-sm text-muted-foreground">
            Polymyth &copy; {new Date().getFullYear()}
          </span>
        </div>
        <TooltipProvider delayDuration={200}>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            {FOOTER_LINKS.map((link) => (
              <Tooltip key={link.key}>
                <TooltipTrigger asChild>
                  {link.to ? (
                    <Link to={link.to} className="hover:text-foreground transition-colors">
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener noreferrer' : undefined}
                      aria-label={typeof link.label !== 'string' ? link.tooltip : undefined}
                      className="hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </a>
                  )}
                </TooltipTrigger>
                <TooltipContent>{link.tooltip}</TooltipContent>
              </Tooltip>
            ))}
          </div>
        </TooltipProvider>
      </div>
    </footer>
  );
}
